import * as Crypto from "expo-crypto";
import * as Device from "expo-device";
import * as SecureStore from "expo-secure-store";
import { Platform } from "react-native";

import type { ActivateDeviceRequest, DeviceFormFactor } from "@recital-master/shared";

import {
  describeNativeOperatingSystem,
  describeWebDevice,
  type WebDeviceDescription
} from "./deviceDescription";

export type ClientDeviceIdentity = ActivateDeviceRequest;

const DEVICE_ID_KEY = "recital-master.deviceId.v1";

function cleanDeviceLabel(value: string | null): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, 80).trim();
}

async function readDeviceId(): Promise<string | null> {
  if (Platform.OS === "web") {
    return typeof localStorage !== "undefined" ? localStorage.getItem(DEVICE_ID_KEY) : null;
  }
  return SecureStore.getItemAsync(DEVICE_ID_KEY);
}

async function writeDeviceId(deviceId: string) {
  if (Platform.OS === "web") {
    if (typeof localStorage !== "undefined") localStorage.setItem(DEVICE_ID_KEY, deviceId);
    return;
  }
  await SecureStore.setItemAsync(DEVICE_ID_KEY, deviceId);
}

function webDeviceDescription(): WebDeviceDescription {
  if (typeof navigator === "undefined") {
    return {
      formFactor: "computer",
      operatingSystem: "Web",
      modelCode: null,
      manufacturer: null,
      browserName: "Browser"
    };
  }
  const userAgentData = (
    navigator as Navigator & { userAgentData?: { mobile?: boolean; platform?: string } }
  ).userAgentData;
  return describeWebDevice({
    userAgent: navigator.userAgent ?? "",
    platform: userAgentData?.platform ?? navigator.platform ?? "",
    mobile: typeof userAgentData?.mobile === "boolean" ? userAgentData.mobile : null,
    maxTouchPoints: navigator.maxTouchPoints ?? 0
  });
}

function nativeFormFactor(): DeviceFormFactor {
  if (Device.deviceType === Device.DeviceType.PHONE) return "phone";
  if (Device.deviceType === Device.DeviceType.TABLET) return "tablet";
  if (Device.deviceType === Device.DeviceType.DESKTOP) return "computer";
  if (Device.deviceType === Device.DeviceType.TV) return "tv";
  return "unknown";
}

export async function getClientDeviceIdentity(): Promise<ClientDeviceIdentity> {
  let deviceId = await readDeviceId();
  if (!deviceId) {
    deviceId = Crypto.randomUUID();
    await writeDeviceId(deviceId);
  }

  if (Platform.OS === "web") {
    return { deviceId, platform: "web", ...webDeviceDescription() };
  }

  const platform = Platform.OS === "ios" ? "ios" : "android";
  const formFactor = nativeFormFactor();
  return {
    deviceId,
    platform,
    formFactor,
    operatingSystem: describeNativeOperatingSystem(platform, Device.osName),
    modelCode: cleanDeviceLabel(Device.modelName),
    manufacturer: cleanDeviceLabel(Device.manufacturer),
    browserName: null
  };
}
